import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Collapsible section used inside multi-field create/edit modals.
 *
 * Default-open for critical sections, default-collapsed for the
 * "more fields" overflow so the modal doesn't feel like a wall of
 * inputs. Keeps the form readable while still exposing every field
 * the schema supports.
 */
interface Props {
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

export function FormSection({ title, subtitle, defaultOpen = true, children }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-accent/30"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          )}
          <span className="text-sm font-semibold">{title}</span>
        </span>
        {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
      </button>
      {open && <div className="space-y-3 border-t border-border p-3">{children}</div>}
    </section>
  );
}

/**
 * Form field row that handles label + required marker + inline error
 * + optional hint, all with consistent spacing.
 */
export function FormField({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

/**
 * Grid wrapper for 2-column field layouts on desktop, single column
 * on mobile.
 */
export function FormGrid({ children, cols = 2 }: { children: ReactNode; cols?: 2 | 3 }) {
  const className = cols === 3 ? 'grid gap-3 sm:grid-cols-3' : 'grid gap-3 sm:grid-cols-2';
  return <div className={className}>{children}</div>;
}
